import React, { useEffect, useState } from 'react'
import { useParams, generatePath } from 'react-router'
import { Link } from 'react-router-dom'
import api from 'helpers/api'
import { IOrganisation, IOrganisationHub, ISitesModalOrganisation } from 'types'
import { useApplicationStore } from 'hooks/application'
import { Tick, Users, Globe, Subscription, Lock, Organisation as OrganisationIcon } from 'components/icons'
import { APP_URL, OrganisationRoles, PROJECT } from 'app-constants'
import { capitaliseFirstLetter, validateSourceUrl, exportToCsv } from 'helpers/utils'
import { getDifferenceInDays, formatShortDate, formatShortDateTime } from 'helpers/date'
import Button from 'components/button'
import { HOME, PROFILE, ORGANISATION_API_KEYS, ORGANISATION_TEAMS } from 'routes'
import OrganisationSitesModal from 'views/organisation/components/sites'
import OrganisationAdminsModal from 'views/organisation/components/admins'
import OrganisationLoginModal from 'views/organisation/components/login'
import SecuritySettingsModal from 'views/organisation/components/security'
import Select from 'components/select'
import Skeleton from 'components/skeleton'
import StatsCard from 'components/statsCard'
import DataPanel from 'components/dataPanel'

const Organisation: React.FC = () => {
  const params: { organisationId?: string } = useParams()

  const { setSnackbarMessage, displayErrorMessage, sidebarWidth } = useApplicationStore()

  const [loading, setLoading] = useState<boolean>(false)
  const [organisation, setOrganisation] = useState<IOrganisation>()
  const [sitesModalOpen, setSitesModalOpen] = useState<boolean>(false)
  const [adminsModalOpen, setAdminsModalOpen] = useState<boolean>(false)
  const [loginModalOpen, setLoginModalOpen] = useState<boolean>(false)
  const [securitySettingsModalOpen, setSecuritySettingsModalOpen] = useState<boolean>(false)
  const [userSearch, setUserSearch] = useState<string>('')
  const [hubSearch, setHubSearch] = useState<string>('')

  useEffect(() => {
    if (!params.organisationId) return
    setLoading(true)
    api
      .getOrganisation(params.organisationId)
      .then((response) => setOrganisation(response.data))
      .catch(displayErrorMessage)
      .finally(() => setLoading(false))
  }, [params.organisationId, displayErrorMessage])

  const handleOrganisationChange = (updated: ISitesModalOrganisation) => {
    setOrganisation(updated as IOrganisation)
  }

  const upgradeUserLicense = (userPublicId: string) => {
    if (!organisation) return

    api
      .allocateOrganisationLicense(organisation.publicId, userPublicId)
      .then(() => {
        setOrganisation({
          ...organisation,
          users: organisation.users.map((u) => {
            if (u.user.publicId === userPublicId) {
              return {
                ...u,
                user: {
                  ...u.user,
                  subscriptionLevel: 1,
                  userLevelSubscription: 1,
                  isOnFreeTrial: false,
                  organisationTeamId: organisation.teams?.find((t) => t.name === 'Default')?.publicId || '—',
                  organisationTeamName: 'Default'
                }
              }
            }
            return u
          })
        })
      })
      .catch((error) => {
        displayErrorMessage(error)
      })
  }

  const downgradeUserLicense = (userPublicId: string) => {
    if (!organisation) return

    api
      .removeOrganisationLicense(organisation.publicId, userPublicId)
      .then(() => {
        setOrganisation({
          ...organisation,
          users: organisation.users.map((u) => {
            if (u.user.publicId === userPublicId) {
              return {
                ...u,
                user: {
                  ...u.user,
                  subscriptionLevel: 0,
                  userLevelSubscription: 0,
                  isOnFreeTrial: false,
                  organisationTeamId: null,
                  organisationTeamName: null
                }
              }
            }
            return u
          })
        })
      })
      .catch((error) => {
        displayErrorMessage(error)
      })
  }

  const changeUserTeam = (userEmail: string, userPublicId: string, teamId: string, userName: string) => {
    if (!organisation || !params.organisationId) return
    api
      .assignTeamSeat(teamId, userEmail)
      .then(() => {
        setSnackbarMessage({ status: 'success', message: `${userName} moved to team` })
        setOrganisation({
          ...organisation,
          users: organisation.users.map((u) => {
            if (u.user.publicId === userPublicId) {
              return {
                ...u,
                user: {
                  ...u.user,
                  organisationTeamId: teamId,
                  organisationTeamName: organisation.teams?.find((t) => t.publicId === teamId)?.name || '—'
                }
              }
            }
            return u
          })
        })
      })
      .catch(displayErrorMessage)
  }

  const changeHubSite = (hub: IOrganisationHub, newSitePublicId: string) => {
    if (!organisation || !newSitePublicId || hub.site?.publicId === newSitePublicId) return

    // A hub cannot be detached from its site, so only a real site is ever sent.
    const newSite = organisation.sites.find((s) => s.publicId === newSitePublicId)
    if (!newSite?.publicId) return

    const currentSite = hub.site ? organisation.sites.find((s) => s.publicId === hub.site!.publicId) : null

    const currentTeamId = currentSite?.teamId ?? null
    const newTeamId = newSite.teamId ?? null
    const teamsAreDifferent = currentTeamId !== newTeamId && (currentTeamId !== null || newTeamId !== null)

    if (teamsAreDifferent) {
      const fromLabel = currentSite?.teamName || 'no team'
      const toLabel = newSite.teamName || 'no team'
      const confirmed = window.confirm(
        `This will move "${hub.name}" from a site on ${fromLabel} to a site on ${toLabel}. ` +
          `Site admins on the old site will lose implicit access, and site admins on the new site will gain it. ` +
          `Direct hub members will keep their access.\n\nContinue?`
      )
      if (!confirmed) return
    }

    api
      .updateProject({ publicId: hub.publicId, site: { publicId: newSite.publicId } })
      .then(() => {
        setSnackbarMessage({
          status: 'success',
          message: `${hub.name} moved to ${newSite.name}`
        })
        setOrganisation({
          ...organisation,
          hubs: organisation.hubs.map((h) => {
            if (h.publicId === hub.publicId) {
              return { ...h, site: newSite }
            }
            return h
          })
        })
      })
      .catch(displayErrorMessage)
  }

  const deleteUser = (userPublicId: string, userName: string, userEmail: string) => {
    if (!organisation) return

    if (
      !window.confirm(
        `Are you sure you want to delete ${userName}? This will remove them from all ${PROJECT}s. This is not reversible.`
      )
    ) {
      return
    }

    api
      .deleteOrganisationUser(organisation.publicId, userPublicId)
      .then(() => {
        setSnackbarMessage({
          status: 'success',
          message: `${userName} (${userEmail}) deleted successfully.`
        })
        setOrganisation({
          ...organisation,
          users: organisation.users.filter((u) => u.user.publicId !== userPublicId)
        })
      })
      .catch((error) => {
        displayErrorMessage(error)
      })
  }

  // 2) Inside the component (after organisation check guards)
  const users = organisation?.users ?? []
  const hubs = organisation?.hubs ?? []

  const computeFilteredUsers = () => {
    if (!userSearch.trim()) return users
    const searchLower = userSearch.toLowerCase()
    return users.filter((u) => {
      if (u.user.name?.toLowerCase().includes(searchLower)) return true
      if (u.user.email?.toLowerCase().includes(searchLower)) return true
      if (u.role?.toLowerCase().includes(searchLower)) return true
      return false
    })
  }
  const filteredUsers = computeFilteredUsers()

  const computeFilteredHubs = () => {
    if (!hubSearch.trim()) return hubs
    const searchLower = hubSearch.toLowerCase()
    return hubs.filter((h) => {
      if (h.name?.toLowerCase().includes(searchLower)) return true
      if (h.site?.name?.toLowerCase().includes(searchLower)) return true
      return false
    })
  }
  const filteredHubs = computeFilteredHubs()

  const handleExportUsers = () => {
    if (!organisation) return
    const headers = ['Name', 'Email', 'Signed Up', 'Last Login', 'Sites', 'Org Role', 'Paid User', 'Team']
    const rows = filteredUsers.map((u) => [
      u.user.name || '',
      u.user.email || '',
      new Date(u.user.createdAt).toLocaleDateString(),
      u.user.lastSignedIn ? new Date(u.user.lastSignedIn).toLocaleString() : 'Never',
      (u.sites ?? []).map((s) => s.name).join(', '),
      capitaliseFirstLetter(u.role),
      u.user.subscriptionLevel === 1 ? 'Yes' : 'No',
      u.user?.organisationTeamName || '—'
    ])
    exportToCsv(`${organisation.name} - Users.csv`, headers, rows)
  }

  const handleExportHubs = () => {
    if (!organisation) return
    const headers = ['Name', 'Created At', 'Site', 'Is Public', 'MFA Required', 'Domains Access']
    const rows = filteredHubs.map((hub) => [
      hub.name,
      new Date(hub.createdAt).toLocaleDateString(),
      hub.site ? hub.site.name : 'No site',
      hub.public ? 'Yes' : 'No',
      hub.mfaRequired ? 'Yes' : 'No',
      (hub.domainsAccess ?? []).join(', ')
    ])
    exportToCsv(`${organisation.name} - ${capitaliseFirstLetter(PROJECT)}s.csv`, headers, rows)
  }

  const getUserTableData = () =>
    filteredUsers.map((user) => ({
      profilePicture: {
        value: user.user.email,
        label: (
          <img
            src={validateSourceUrl(user.user.profilePicture)}
            className="object-cover rounded-full mr-2.5 w-10 h-10"
            alt={`${user.user.name ? user.user.name : 'Anonymous'}`}
          />
        )
      },
      email: { value: user.user.email, label: user.user.email },
      createdAt: {
        value: user.user.createdAt,
        label: formatShortDate(user.user.createdAt)
      },
      lastSignedIn: {
        value: user.user.lastSignedIn || '',
        label: user.user.lastSignedIn
          ? (() => {
              const days = getDifferenceInDays(new Date(), new Date(user.user.lastSignedIn))
              const suffix =
                !Number.isFinite(days) || days < 0
                  ? ''
                  : days === 0
                    ? ' (today)'
                    : ` (${days} day${days === 1 ? '' : 's'} ago)`
              return `${formatShortDateTime(user.user.lastSignedIn)}${suffix}`
            })()
          : 'Never'
      },
      sites: {
        value: (user.sites ?? []).map((s) => s.name).join(', '),
        label:
          user.sites && user.sites.length > 0 ? (
            <div className="flex flex-col">
              {user.sites.map((s, i) => (
                <span key={s.publicId}>
                  {s.name}
                  {i < user.sites.length - 1 ? ',' : ''}
                </span>
              ))}
            </div>
          ) : (
            '-'
          )
      },
      name: {
        value: user.user.name,
        label: (
          <span className="flex items-center gap-2">
            {user.user.name}
            {user.isPending && (
              <span className="text-xs text-orange bg-really-light-yellow px-2 py-0.5 rounded-full font-bold">
                Invited
              </span>
            )}
            {user.isExternal && !user.isPending && (
              <span className="text-xs text-blue bg-really-light-blue px-2 py-0.5 rounded-full font-bold">
                External
              </span>
            )}
          </span>
        )
      },
      role: { value: user.role, label: capitaliseFirstLetter(user.role) },
      paidUser: {
        label: user.isPending ? (
          '—'
        ) : (
          <Select
            options={[
              { label: 'Yes', value: 'yes' },
              { label: 'No', value: 'no' }
            ]}
            optionsSelected={[user.user.userLevelSubscription === 1 ? 'yes' : 'no']}
            onOptionClick={(option) => {
              if (option === 'no' && user.user.userLevelSubscription === 1) {
                downgradeUserLicense(user.user.publicId)
              } else if (option === 'yes' && user.user.userLevelSubscription === 0) {
                upgradeUserLicense(user.user.publicId)
              }
            }}
          />
        ),
        value: user.isPending ? '—' : user.user.userLevelSubscription === 1 ? 'Yes' : 'No'
      },
      team: {
        label: user.isPending ? (
          user.user?.organisationTeamName || '—'
        ) : user.user.userLevelSubscription === 1 ? (
          <Select
            options={(organisation!.teams || []).map((t) => ({ label: t.name, value: t.publicId }))}
            optionsSelected={user.user?.organisationTeamId ? [user.user.organisationTeamId] : []}
            onOptionClick={(teamId: string) => {
              if (teamId === user.user?.organisationTeamId) return
              changeUserTeam(user.user.email, user.user.publicId, teamId, user.user.name)
            }}
          />
        ) : (
          '—'
        ),
        value: user.user?.organisationTeamName || '—'
      },
      profile: {
        value: user.user.publicId,
        label: user.user.firebaseUserId ? (
          <Button
            to={generatePath(PROFILE, { userId: user.user.firebaseUserId, tab: 'information' })}
            internalType="outline"
          >
            View Profile
          </Button>
        ) : null
      },
      delete: {
        value: user.user.publicId,
        label: (
          <Button
            internalType="danger"
            onClick={() => deleteUser(user.user.publicId, user.user.name || user.user.email, user.user.email || '')}
          >
            Delete User
          </Button>
        )
      }
    }))

  const userTableColumns = [
    { header: '', id: 'profilePicture' },
    { header: 'Name', id: 'name' },
    { header: 'Email', id: 'email' },
    { header: 'Signed Up', id: 'createdAt' },
    { header: 'Last Signed In', id: 'lastSignedIn' },
    { header: 'Sites', id: 'sites' },
    { header: 'Org Role', id: 'role' },
    { header: 'Paid User', id: 'paidUser' },
    { header: 'Team', id: 'team' },
    { header: 'Profile', id: 'profile' },
    { header: '', id: 'delete' }
  ]

  const getHubTableData = () =>
    filteredHubs.map((hub) => ({
      name: { value: hub.name, label: hub.name },
      createdAt: {
        value: hub.createdAt,
        label: formatShortDate(hub.createdAt)
      },
      isPublic: { value: hub.public ? 'Yes' : 'No', label: hub.public ? 'Yes' : 'No' },
      mfaRequired: { value: hub.mfaRequired ? 'Yes' : 'No', label: hub.mfaRequired ? 'Yes' : 'No' },
      domainsAccess: { value: (hub.domainsAccess ?? []).join(', '), label: (hub.domainsAccess ?? []).join(', ') },
      site: {
        value: hub.site ? hub.site.name : 'No site',
        label: (
          <Select
            options={organisation!.sites.filter((s) => s.publicId).map((s) => ({ label: s.name, value: s.publicId! }))}
            optionsSelected={hub.site?.publicId ? [hub.site.publicId] : []}
            placeholder="No site"
            onOptionClick={(value) => changeHubSite(hub, value)}
          />
        )
      },
      access: {
        value: hub.publicId,
        label: (
          <a href={`${APP_URL}/hub/${hub.publicId}`} target="_blank" rel="noopener noreferrer">
            <Button internalType="outline">Go to {capitaliseFirstLetter(PROJECT)}</Button>
          </a>
        )
      }
    }))

  const hubTableColumns = [
    { header: 'Name', id: 'name' },
    { header: 'Created At', id: 'createdAt' },
    { header: 'Site', id: 'site' },
    { header: 'Is Public?', id: 'isPublic' },
    { header: 'MFA Required?', id: 'mfaRequired' },
    { header: 'Domains Access', id: 'domainsAccess' },
    { header: 'Access', id: 'access' }
  ]

  if (loading) {
    return (
      <div className="w-full p-5 flex flex-col gap-4">
        <div className="flex justify-between flex-wrap items-center gap-4">
          <Skeleton className="h-10 w-[200px]" />
        </div>

        <div className="flex flex-wrap gap-4">
          <Skeleton className="flex-1 min-w-[220px] h-[168px]" />
          <Skeleton className="flex-1 min-w-[220px] h-[168px]" />
          <Skeleton className="flex-1 min-w-[220px] h-[168px]" />
          <Skeleton className="flex-1 min-w-[220px] h-[168px]" />
          <Skeleton className="flex-1 min-w-[220px] h-[168px]" />
        </div>
      </div>
    )
  } else if (!organisation && !loading) {
    return (
      <div className="bg-surface flex flex-1 items-center justify-center overflow-hidden box-border h-screen w-full px-20">
        <div className="flex flex-col w-full justify-center items-center">
          <div className="text-[2rem]">Organisation not found.</div>
          <Button className="mt-4" to={HOME}>
            Return To Home
          </Button>
        </div>
      </div>
    )
  } else if (organisation) {
    const usersWithPaidLicenses = organisation.users.filter((user) => user.user.userLevelSubscription === 1)

    const parseLogin = (enforceLogin: string) => {
      if (enforceLogin === 'saml.morta.io') {
        return 'Single Sign On'
      }
      if (enforceLogin === 'morta.io') {
        return 'Email/Password'
      }
      if (enforceLogin === 'google.com') {
        return 'Google'
      }
      if (enforceLogin === 'microsoft.com') {
        return 'Microsoft'
      }
      return 'Any'
    }

    return (
      <div className="w-full py-5 px-[30px] flex flex-col gap-4 h-screen min-h-0 max-xl:h-auto max-xl:overflow-auto">
        {/* Header */}
        <div className="flex flex-wrap items-center gap-2">
          <h1 className={`mr-auto ${sidebarWidth === 0 ? 'ml-9' : ''}`}>{organisation.name}</h1>

          <span className="bg-green text-on-light rounded-lg px-2.5 py-2 text-sm font-semibold self-center">
            {organisation.planTier === 'scale_up'
              ? 'Scale Up Plan'
              : organisation.planTier === 'team'
                ? 'Team Plan'
                : 'Professional Plan'}{' '}
            <Tick className="h-4 w-4" />
          </span>

          <Button
            to={generatePath(ORGANISATION_API_KEYS, { organisationId: organisation.publicId })}
            internalType="outline"
          >
            Manage API Keys
          </Button>

          <Button onClick={() => setSecuritySettingsModalOpen(true)} internalType="primary">
            Security Settings
          </Button>
        </div>

        {/* KPI Cards */}
        <div className="flex flex-wrap gap-4">
          <StatsCard
            icon={<Globe className="h-[18px] w-[18px]" />}
            className="flex-1 min-w-[220px]"
            label="Domains"
            value={organisation.domains?.join(', ') || 'No domains'}
            sub="All domains associated with this organisation"
            footer={
              <i className="flex items-center text-xs h-[34px]">
                Contact your account manager to update your organisation domains.
              </i>
            }
          />

          <StatsCard
            icon={<Users className="h-[18px] w-[18px]" />}
            className="flex-1 min-w-[220px]"
            label="# Admins"
            value={organisation.users.filter((user) => user.role === OrganisationRoles.ADMIN).length}
            sub={`Admins have access to all ${PROJECT}s under sites in this organisation`}
            footer={
              <Button internalType="outline" onClick={() => setAdminsModalOpen(true)}>
                Manage Admins
              </Button>
            }
          />

          <StatsCard
            icon={<Subscription className="h-[18px] w-[18px]" />}
            className="flex-1 min-w-[220px]"
            label="# Paid Users / Seats"
            value={`${usersWithPaidLicenses.length} / ${organisation.paidUserSeats}`}
            sub={`Paid users have access to additional features`}
            footer={
              <Link to={generatePath(ORGANISATION_TEAMS, { organisationId: organisation.publicId })}>
                <Button internalType="outline">Manage Teams</Button>
              </Link>
            }
          />

          <StatsCard
            icon={<Lock className="h-[18px] w-[18px]" />}
            className="flex-1 min-w-[220px]"
            label="Login Method"
            value={organisation.enforceLogin ? parseLogin(organisation.enforceLogin) : 'Any'}
            sub="The method of authentication for this organisation"
            footer={
              <Button internalType="outline" onClick={() => setLoginModalOpen(true)}>
                Manage Login
              </Button>
            }
          />

          <StatsCard
            icon={<OrganisationIcon className="h-[18px] w-[18px]" />}
            className="flex-1 min-w-[220px]"
            label="# Sites"
            value={organisation.sites.length}
            sub="Number of sites associated with this organisation"
            footer={
              <Button
                internalType="outline"
                onClick={() => setSitesModalOpen(true)}
                disabled={organisation.planTier === 'professional'}
              >
                Manage Sites
              </Button>
            }
          />
        </div>

        <div className="grid grid-cols-1 xl:grid-cols-2 gap-8 xl:gap-4 xl:flex-1 xl:min-h-0 xl:overflow-hidden pt-2.5 pb-2.5 pl-0.5 pr-0.5">
          <DataPanel
            title={`${organisation.users.length} Users`}
            items={users}
            getDate={(u) => u.user.createdAt}
            chartLabel="Cumulative users"
            tableData={getUserTableData()}
            tableColumns={userTableColumns}
            searchPlaceholder="Search for a user..."
            search={userSearch}
            onSearchChange={setUserSearch}
            onExport={handleExportUsers}
            fullscreen
          />

          <DataPanel
            title={`${organisation.hubs.length} ${capitaliseFirstLetter(PROJECT)}s (associated with sites)`}
            items={hubs}
            getDate={(h) => h.createdAt}
            chartLabel={`Cumulative ${capitaliseFirstLetter(PROJECT)}s`}
            tableData={getHubTableData()}
            tableColumns={hubTableColumns}
            searchPlaceholder="Search for a hub..."
            search={hubSearch}
            onSearchChange={setHubSearch}
            onExport={handleExportHubs}
            fullscreen
          />
        </div>
        {sitesModalOpen && (
          <OrganisationSitesModal
            open={sitesModalOpen}
            onClose={() => setSitesModalOpen(false)}
            organisation={organisation}
            onChanged={handleOrganisationChange}
          />
        )}

        {adminsModalOpen && (
          <OrganisationAdminsModal
            open={adminsModalOpen}
            onClose={() => setAdminsModalOpen(false)}
            organisation={organisation}
            onChanged={handleOrganisationChange}
          />
        )}

        {loginModalOpen && (
          <OrganisationLoginModal
            open={loginModalOpen}
            onClose={() => setLoginModalOpen(false)}
            organisation={organisation}
            onChanged={handleOrganisationChange}
          />
        )}

        {securitySettingsModalOpen && (
          <SecuritySettingsModal
            open={securitySettingsModalOpen}
            onClose={() => setSecuritySettingsModalOpen(false)}
            organisation={organisation}
            onChanged={handleOrganisationChange}
          />
        )}
      </div>
    )
  }

  return null
}

export default Organisation
